import { Box, InputBase, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import type { ClinicalContextField } from '../model';

/** One clinical-context field in the worklist's input style. */
function ContextField({
    label,
    value,
    onChange,
    maxLength,
    disabled,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    maxLength: number;
    disabled: boolean;
}) {
    return (
        <Box sx={{ flex: 1 }}>
            <Typography
                component="label"
                sx={{
                    display: 'block',
                    fontSize: 11,
                    color: 'text.secondary',
                    mb: 0.5,
                }}
            >
                {label}
            </Typography>
            <InputBase
                value={value}
                onChange={(event) => onChange(event.target.value)}
                disabled={disabled}
                inputProps={{ 'aria-label': label, maxLength }}
                sx={{
                    width: '100%',
                    fontSize: 12.5,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.sunken',
                    px: 1,
                    // No hover/focus accent while disabled: an accented border would read as editable.
                    '&:hover:not(.Mui-disabled)': {
                        borderColor: 'primary.main',
                    },
                    '&.Mui-focused': { borderColor: 'primary.main' },
                    '& .MuiInputBase-input': { py: 0.5, px: 0 },
                }}
            />
        </Box>
    );
}

type ClinicalContextFieldsProps = {
    notes: string;
    /** Locked while this entry's files are uploading. */
    disabled: boolean;
    onChange: (field: ClinicalContextField, value: string) => void;
};

/** The context this drop's reviewer attaches to one patient, collected below the dataset tree. */
export default function ClinicalContextFields({
    notes,
    disabled,
    onChange,
}: ClinicalContextFieldsProps) {
    const intl = useIntl();
    return (
        <Box
            sx={{
                flexShrink: 0,
                py: 2,
                px: 2.5,
                borderTop: 1,
                borderColor: 'divider',
            }}
        >
            {/* The field is locked while this entry's files upload; say so,
                or a greyed-out field reads as broken. */}
            {disabled && (
                <Typography
                    sx={{ fontSize: 12, color: 'text.disabled', mb: 1.25 }}
                >
                    {intl.formatMessage({
                        id: 'patientList.import.context.locked',
                    })}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: 1.25,
                }}
            >
                <ContextField
                    label={intl.formatMessage({
                        id: 'patientList.import.context.notes',
                    })}
                    value={notes}
                    onChange={(value) => onChange('notes', value)}
                    maxLength={2048}
                    disabled={disabled}
                />
            </Box>
        </Box>
    );
}
